import React, { useCallback } from 'react';
import { Network, Factory, PackageCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { craftHeritage, artisanImages } from '@/data/mock';
import useEmblaCarousel from 'embla-carousel-react';
import { Button } from '@/components/ui/button';

const iconMap = {
  Network: Network,
  Factory: Factory,
  PackageCheck: PackageCheck
};

const CraftHeritageSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'center' });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <section className="py-20 md:py-32 bg-background relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-accent/10 via-transparent to-accent/10 pointer-events-none" />
      
      <div className="container relative mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            India's Product Heritage & Manufacturing Strength
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Combining traditional craftsmanship with modern manufacturing across India's export-ready supply clusters
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
          {craftHeritage.map((item, index) => {
            const IconComponent = iconMap[item.icon];
            return (
              <div
                key={item.id}
                className="group text-center p-8 rounded-2xl bg-background/50 backdrop-blur-sm border border-border/50 hover:border-border hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-foreground/5 dark:bg-amber-600/20 group-hover:bg-foreground/10 dark:group-hover:bg-amber-600/30 transition-colors">
                  <IconComponent className="h-8 w-8 dark:text-amber-500" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Production Network Carousel */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8">Production Across India's Supplier Network</h3>
          
          <div className="relative">
            <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
              <div className="flex">
                {artisanImages.map((image) => (
                  <div key={image.id} className="flex-[0_0_100%] min-w-0 relative">
                    <div className="relative h-64 md:h-96 shadow-2xl">
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                        <p className="text-lg font-medium text-foreground">{image.caption}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carousel Controls */}
            <Button
              variant="outline"
              size="icon"
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background w-10 h-10 md:w-12 md:h-12"
              onClick={scrollPrev}
            >
              <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background w-10 h-10 md:w-12 md:h-12"
              onClick={scrollNext}
            >
              <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-4 px-4">
            Diverse production capabilities across India's export clusters
          </p>
        </div>
      </div>
    </section>
  );
};

export default CraftHeritageSection;
